<template>
  <div
    v-if="activeSubNavigation !== SubNavigations.Settings"
    class="formula-box"
    :style="{ top: `${boxPosition.top}px`, left: `${boxPosition.left}px` }"
  >
    <div class="draggable-box">
      <div class="header" @mousedown="startDrag">
        <i class="icon-Dropdown-5"></i>
        <i class="icon-close" @click="handleEscapeKey"></i>
      </div>
      <div class="content">
        <textarea
          ref="textarea"
          :data-id="activeCellId"
          :rows="textAreaRows"
          :value="text"
          :class="{
            'formula-box-pointer-disable': editMode === EditModes.Disable,
            'formula-box-inward-cursor': editMode === EditModes.InnerEdit,
          }"
          @input="editCellText"
          @keyup="setRange"
          @keydown="handleKeyDown"
        ></textarea>
      </div>
    </div>
    <!-- Formula Tooltip -->
    <formula-tooltip
      v-if="editMode !== EditModes?.Disable"
      :formula-text="formulaText"
      :cursor-position="cursorPosition"
      :cell-id="id"
      :styles="{
        top: `${textareaHeight}px`,
        left: `${0}px`,
      }"
      class="floating-tooltip"
    />

    <!-- Formula Search - Now Positioned Correctly -->
    <formula-search
      v-if="showSearchBox"
      :collection="collection"
      :input-text="inputText"
      :styles="{}"
      :searched-items="dropDownFilteredList"
      class="floating-search"
      @select-formula="selectFormula"
    />
  </div>
</template>

<script>
import DesignerMixin from "../../process-design/DesignerMixin";
import EditCellMixin from "../../process-design/EditCellMixin";
import hotkeys from "hotkeys-js";
import { SubNavigations, Preferences, HotkeyScopes, EditModes, keycodeToDirection } from "../../../utils/constants";

import FormulaTooltip from "../../process-design/formula/FormulaTooltip.vue";
import FormulaSearch from "../../process-design/formula/FormulaSearch.vue";
import { isRangeSelectionAllowed, cellIndexToLabel } from "../../../utils/util";
import { getEditModeValue } from "../../../utils/DataTypeUtils";
export default {
  components: { FormulaTooltip, FormulaSearch },
  mixins: [DesignerMixin, EditCellMixin],
  data() {
    return {
      SubNavigations: SubNavigations,
      isDragging: false,
      offset: { x: 0, y: 0 }, // Offset to calculate the mouse position relative to the box
      textareaHeight: 500,
      previousScope: null,
    };
  },
  computed: {
    boxPosition() {
      return this.$store.state.preferences.allPreferences?.formulaBoxPosition || { top: 100, left: 700 };
    },
    activeSubNavigation() {
      return this.$store.state.preferences.allPreferences?.activeSubNavigation;
    },
    textAreaRows() {
      return (String(this.text).match(/\n/g)?.length || 0) + 1;
    },
    editMode() {
      return this.$store.state.pdesign.formulaBoxEditMode.value;
    },
  },
  watch: {
    // Update text when activeCellId changes
    activeCellId(newValue, oldValue) {
      if (newValue !== null && newValue !== oldValue) {
        this.updateTextFromStore();
        this.initializeEditMode();
        this.initializeData(newValue);
      }
    },
    // Update text when editCell changes (e.g., formula updates)
    editCell: {
      deep: true, // Ensures changes inside objects are detected
      handler(newValue, oldValue) {
        if (newValue && newValue.text !== oldValue?.text) {
          this.updateTextFromStore();
        }
      },
    },
  },
  mounted() {
    this.$refs.textarea.focus();
    this.initializeData(this.editCell.id);
    this.$nextTick(() => {
      this.previousScope = hotkeys.getScope();
      this.$store.dispatch("setScope", HotkeyScopes.FormulaBox);
      this.text = this.editCell.text;
      this.$store.dispatch("cells/setFormulaRange", { title: this.editCell.text, isMainRange: false });
    });
  },
  beforeUnmount() {
    this.$store.dispatch("setScope", this.previousScope);
  },
  methods: {
    updateTextFromStore() {
      if (!this.activeCellId) return;
      const [columnIndex, rowIndex] = this.getCellIndexById(this.activeCellId);
      const currentDataType = this.$store.getters["rangeStyle/dataType"](columnIndex, rowIndex);
      const cellText = this.$store.state.cells.editCell?.text || "";

      this.text = getEditModeValue(currentDataType, cellText) || "";
    },
    async handleKeyDown(event) {
      if (hotkeys.getScope() === HotkeyScopes.QuickAccessToolbar) return;

      if (this.editMode === EditModes.Disable) {
        this.initializeEditMode();
      }
      if (this.handleHotkeys(event)) return;

      if (keycodeToDirection[event.key]) {
        this.handleDirectionKeys(event);
      } else if (event.key === "Enter") {
        await this.handleEnterKey(event);
      } else if (event.key === "Tab") {
        this.handleTabKey(event);
      } else if (event.key === "Escape" && this.editMode !== EditModes.Disable) {
        this.handleEscapeKey();
      } else if (event.ctrlKey && event.key === ".") hotkeys.trigger("ctrl+.", HotkeyScopes.ProcessDesigner);
      else if (event.key === "F4") {
        this.handleF4Key(event);
      } else if (event.key === "F2") {
        this.handleF2Key(event);
      }
    },
    async handleEnterKey(event) {
      event.preventDefault();

      if (event.ctrlKey) {
        await this.processCtrlEnter();
        this.reset();
      } else if (event.altKey) {
        this.insertNewLine(event);
      } else if (event.shiftKey) {
        this.blur(event);
        hotkeys.trigger("shift+enter", HotkeyScopes.ProcessDesigner);
      }
      // else if (this.showSearchBox && this.dropDownFilteredList.length > 0) {
      //   hotkeys.trigger("tab", HotkeyScopes.FormulaSearch);
      // }
      else {
        this.blur(event);
        await this.handleRegularEnter();
      }

      this.formulaText = "";
    },

    async handleRegularEnter() {
      // Specific to second component
      this.$store.dispatch("cells/setActiveRangeMainCellCoordinate", this.getCellCoordinate(this.editCell.id));

      hotkeys.trigger("enter", HotkeyScopes.ProcessDesigner);
      this.$store.dispatch("setScope", this.previousScope);

      await this.$nextTick();
      this.$store.dispatch("pdesign/toggleFormulaBox");

      this.$nextTick(() => {
        this.reset();
      });
    },
    handleF2Key(event) {
      let cursorPosition = event.target.selectionStart;

      if (this.editMode === EditModes.InnerEdit) {
        this.$store.dispatch("pdesign/setFormulaBoxEditMode", EditModes.OuterEdit);
        this.$store.dispatch("cells/setEditCell", { cursorPosition, focus: true });
      } else {
        this.$store.dispatch("pdesign/setFormulaBoxEditMode", EditModes.InnerEdit);
        this.$store.dispatch("cells/setEditCell", { range: "", focus: true });
      }
    },
    handleDirectionKeys(event) {
      // If basic direction key handling is done, return
      if (this.handleBasicDirectionKeys(event)) return;

      if (this.editMode === EditModes.InnerEdit) {
        if (hotkeys.getScope() === HotkeyScopes.FormulaSearch) {
          event.preventDefault();
          hotkeys.trigger(keycodeToDirection[event.key], HotkeyScopes.FormulaSearch);
        }
        // Text area defaults actions is working fine
      } else {
        if (this.editMode === EditModes.Disable) {
          this.$store.dispatch("pdesign/setFormulaBoxEditMode", EditModes.OuterEdit);
        } else {
          this.$store.dispatch("cells/editCellContent", {
            id: this.cellId,
            value: event.target.value,
          });
          this.$store.dispatch("cells/setEditCell", {
            cursorPosition: event.target.selectionStart,
            formulaRangeSelectionAllowed: isRangeSelectionAllowed(
              event.target.value.substring(0, event.target.selectionStart)
            ),
            range: "",
          });
        }
      }
    },
    handleEscapeKey() {
      this.showSearchBox = false;
      this.text = this.originalText;

      this.$store.dispatch("cells/setEditCell", { text: this.text, focus: false });
      this.$store.dispatch("cells/setFormulaRange", {
        title: `${cellIndexToLabel(this.columnIndex, this.rowIndex)}`,
      });
      this.$store.dispatch("cells/setActiveRangeMainCellCoordinate", this.getCellCoordinate(this.editCell.id));

      this.$nextTick(() => {
        this.reset();
      });
    },
    initializeEditMode() {
      this.$store.dispatch("pdesign/setFormulaBoxEditMode", EditModes.OuterEdit);
      this.$store.dispatch("cells/setEditCell", { focus: true });
      this.$store.dispatch("pdesign/setEditMode", EditModes.OuterEdit);
    },
    initializeData(cellId) {
      if (this.mergedCellIds[cellId]) cellId = this.mergedCellIds[cellId].replace("_merged", "");
      const [columnIndex, rowIndex] = this.getCellIndexById(`${cellId}`);
      const text = this.getCellText(rowIndex, columnIndex);
      this.originalText = text;
      this.text = this.editCell.text;
      this.id = cellId;
    },
    reset() {
      this.$store.dispatch("pdesign/setFormulaBoxEditMode", EditModes.Disable);
      this.$store.dispatch("pdesign/setEditMode", EditModes.Disable);
      this.$store.dispatch("pdesign/toggleFormulaBox", false);
      this.$refs.textarea?.blur();
      this.argNumber = 0;
      this.collection = [...this.allFormulaNames];
      this.showSearchBox = false;
    },
    startDrag(event) {
      this.isDragging = true;
      // // Calculate the offset
      this.offset.x = event.clientX - this.boxPosition.left;
      this.offset.y = event.clientY - this.boxPosition.top;
      document.addEventListener("mousemove", this.onDrag);
      document.addEventListener("mouseup", this.stopDrag);
    },
    onDrag(event) {
      if (!this.isDragging) return;
      // Update the position of the box
      let left = event.clientX - this.offset.x;
      let top = event.clientY - this.offset.y;

      const maxTop = window.innerHeight - 120;
      const maxRight = window.innerWidth - 50;
      const maxLeft = left + 10;

      top = Math.min(maxTop, Math.max(top, 0));
      left = Math.min(maxRight, maxLeft);

      this.$store.dispatch("preferences/updateWithoutPersist", {
        key: Preferences.formulaBoxPosition,
        value: {
          top,
          left,
        },
      });
    },
    stopDrag() {
      if (this.isDragging) this.$store.dispatch("preferences/persist");
      this.isDragging = false;
      // Remove the event listeners
      document.removeEventListener("mousemove", this.onDrag);
      document.removeEventListener("mouseup", this.stopDrag);
      this.$refs.textarea.focus();
    },
  },
};
</script>

<style lang="less" scoped>
@import "../../../assets/styles/variables";

.formula-box {
  position: absolute;
  display: inline-block;
  width: fit-content;
  z-index: 120;
  &-pointer-disable {
    cursor: default;
  }
  &-inward-cursor {
    caret-color: red;
  }
}
.draggable-box {
  resize: both;
  border-radius: 0.5rem;
  overflow: hidden;
  background: rgba(201, 224, 255, 0.68);
  box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.2);
  min-width: 20vw;
  min-height: 40vh;
  max-width: 100vw;
}

.floating-search,
.floating-tooltip {
  position: absolute;
  top: 100%;
  z-index: 200;
}

.header {
  display: flex;
  position: sticky;
  top: 0;
  z-index: 130;
  background: rgba(201, 224, 255, 0.98);
  cursor: grab;
  border-top-right-radius: 0.5rem;
  border-top-left-radius: 0.5rem;
  i {
    margin-left: auto;
  }
}
.header i:last-child {
  padding-right: 5px;
  cursor: pointer;
}

.content {
  height: 95%;
  textarea {
    padding: 0 5px;
    min-height: 99%;
    width: 100%;
    height: auto;
    resize: none;
    overflow: hidden;
    white-space: pre-wrap;
  }
}
</style>
